import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useAmenities } from "@/hooks/useAmenities";
import { Loader2 } from "lucide-react";
import { parseToCheckboxSections } from "../../utils";
import { CheckboxGroup } from "@/components/checkbox-group";
import {
  STATUS_PRIVATE_OFFICE,
  STATUS_PRIVATE_OFFICE_NAMES,
} from "@/constants/status-private-office";
import {
  MANUAL_LIFECYCLE_STATE_OPTIONS,
  LIFECYCLE_STATE_LABELS,
  PrivateOfficeLifecycleState,
} from "@/@types/private-office.types";
import { sendTrackDataServer } from "@/features/analytics/utils/serverTrackData";
import { useAuth } from "@/features/authentication";
import { useLocation } from "react-router-dom";
import { Checkbox } from "@/components/ui/checkbox";

export interface Filters {
  minPrice?: number;
  maxPrice?: number;
  minCapacity?: number;
  maxCapacity?: number;
  privateOfficeStatus?: string[];
  lifecycleStates?: PrivateOfficeLifecycleState[];
  amenitiesSlugs: string[];
  distance: number;
  unitCreditPrice?: number;
  skipRecentUpdatesFilter?: boolean;
}

interface PainelFiltersListProps {
  isOpen: boolean;
  onClose: () => void;
  onApplyFilters?: (filters: Filters) => void;
  onSearchSubmit?: () => void;
}

const ACTIVE_SKIP_RECENT_UPDATES_FILTER = true;

const defaultFilters: Filters = {
  minPrice: undefined,
  maxPrice: undefined,
  minCapacity: undefined,
  maxCapacity: undefined,
  privateOfficeStatus: [STATUS_PRIVATE_OFFICE.AVAILABLE.toString()],
  lifecycleStates: [],
  amenitiesSlugs: [],
  distance: 30,
  unitCreditPrice: 80,
  skipRecentUpdatesFilter: ACTIVE_SKIP_RECENT_UPDATES_FILTER,
};

export const PainelFiltersList = ({
  isOpen,
  onClose,
  onSearchSubmit,
}: PainelFiltersListProps) => {
  const { setValue } = useFormContext();
  const [filters, setFilters] = useState<Filters>(defaultFilters);
  const {
    isLoading: isLoadingAmenities,
    amenities,
    handleSearchAmenities,
  } = useAmenities();
  const { userInfo } = useAuth();
  const location = useLocation();

  const updateFilter = <K extends keyof Filters>(key: K, value: Filters[K]) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleApplyFilters = () => {
    setValue("filters", filters);

    sendTrackDataServer({
      eventName: "SEARCH_FILTER_APPLIED",
      data: {
        user_id: userInfo.user?.id ?? null,
        email: userInfo.profile?.email ?? null,
        capacity_max: filters.maxCapacity,
        capacity_min: filters.minCapacity,
        price_max: filters.maxPrice,
        price_min: filters.minPrice,
        amenities: filters.amenitiesSlugs,
        status: filters.privateOfficeStatus,
        distance: filters.distance,
        unit_credit_price: filters.unitCreditPrice,
        skip_recent_updates_filter: filters.skipRecentUpdatesFilter,
      },
      url: location.pathname,
      productType: "PO",
    });
    if (onSearchSubmit) {
      onSearchSubmit();
    }

    onClose();
  };

  const handleResetFilters = () => {
    setFilters(defaultFilters);
    setValue("filters", defaultFilters);
  };

  const handleCheckboxGroupChange = (
    fieldName:
      | "amenitiesSlugs"
      | "amenitiesSlugsOptional"
      | "privateOfficeStatus"
      | "lifecycleStates",
  ) => {
    return (
      selectedOptions: { sectionTitle: string; selectedIds: string[] }[],
    ) => {
      const allSelectedIds = selectedOptions.flatMap(
        (section) => section.selectedIds,
      );

      if (fieldName === "privateOfficeStatus") {
        const hasUnavailable = allSelectedIds.includes(
          STATUS_PRIVATE_OFFICE.NOT_AVAILABLE.toString(),
        );
        if (!hasUnavailable) {
          setValue("lifecycleStates", []);
          setFilters((prev) => ({
            ...prev,
            lifecycleStates: [],
            [fieldName]: allSelectedIds,
          }));
          setValue(fieldName, allSelectedIds);
          return;
        }
      }

      setValue(fieldName, allSelectedIds);
      setFilters((prev) => ({
        ...prev,
        [fieldName]: allSelectedIds,
      }));
    };
  };

  const showLifecycleFilter = filters.privateOfficeStatus?.includes(
    STATUS_PRIVATE_OFFICE.NOT_AVAILABLE.toString(),
  );

  useEffect(() => {
    handleSearchAmenities();
  }, [handleSearchAmenities]);

  const statusOptions = useMemo(
    () => [
      {
        title: "Status",
        options: [
          {
            id: STATUS_PRIVATE_OFFICE.AVAILABLE.toString(),
            label: STATUS_PRIVATE_OFFICE_NAMES.AVAILABLE.title,
            checked: filters.privateOfficeStatus?.includes(
              STATUS_PRIVATE_OFFICE.AVAILABLE.toString(),
            ),
          },
          {
            id: STATUS_PRIVATE_OFFICE.NOT_AVAILABLE.toString(),
            label: STATUS_PRIVATE_OFFICE_NAMES.NOT_AVAILABLE.title,
            checked: filters.privateOfficeStatus?.includes(
              STATUS_PRIVATE_OFFICE.NOT_AVAILABLE.toString(),
            ),
          },
          {
            id: STATUS_PRIVATE_OFFICE.OCCUPED_WITH_WOBA_CLIENT.toString(),
            label: STATUS_PRIVATE_OFFICE_NAMES.OCCUPED_WITH_WOBA_CLIENT.title,
            checked: filters.privateOfficeStatus?.includes(
              STATUS_PRIVATE_OFFICE.OCCUPED_WITH_WOBA_CLIENT.toString(),
            ),
          },
          {
            id: STATUS_PRIVATE_OFFICE.AVAILABLE_WITHOUT_TP.toString(),
            label: STATUS_PRIVATE_OFFICE_NAMES.AVAILABLE_WITHOUT_TP.title,
            checked: filters.privateOfficeStatus?.includes(
              STATUS_PRIVATE_OFFICE.AVAILABLE_WITHOUT_TP.toString(),
            ),
          },
          {
            id: STATUS_PRIVATE_OFFICE.NOT_AVAILABLE_WITHOUT_TP.toString(),
            label: STATUS_PRIVATE_OFFICE_NAMES.NOT_AVAILABLE_WITHOUT_TP.title,
            checked: filters.privateOfficeStatus?.includes(
              STATUS_PRIVATE_OFFICE.NOT_AVAILABLE_WITHOUT_TP.toString(),
            ),
          },
          {
            id: STATUS_PRIVATE_OFFICE.RESERVED.toString(),
            label: STATUS_PRIVATE_OFFICE_NAMES.RESERVED.title,
            checked: filters.privateOfficeStatus?.includes(
              STATUS_PRIVATE_OFFICE.RESERVED.toString(),
            ),
          },
          {
            id: STATUS_PRIVATE_OFFICE.DELETED.toString(),
            label: STATUS_PRIVATE_OFFICE_NAMES.DELETED.title,
            checked: filters.privateOfficeStatus?.includes(
              STATUS_PRIVATE_OFFICE.DELETED.toString(),
            ),
          },
        ],
        columns: 2 as const,
      },
    ],
    [filters.privateOfficeStatus],
  );

  const lifecycleOptions = useMemo(
    () => [
      {
        title: "Motivo de indisponibilidade",
        options: MANUAL_LIFECYCLE_STATE_OPTIONS.map((v) => ({
          id: v,
          label: LIFECYCLE_STATE_LABELS[v],
          checked: filters.lifecycleStates?.includes(v) ?? false,
        })),
        columns: 1 as const,
      },
    ],
    [filters.lifecycleStates],
  );

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-[60]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-500"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-500"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto relative w-screen max-w-[40rem] bg-white">
                  <div className="flex justify-between pt-4 px-4">
                    <div className="flex gap-2 flex-col px-4">
                      <Dialog.Title className="text-xl font-semibold text-zinc-950">
                        Filtros
                      </Dialog.Title>
                      <p className="text-sm text-zinc-500">
                        Use os filtros para encontrar espaços que realmente
                        fazem sentido para você. Quanto mais preciso, melhores
                        os resultados.
                      </p>
                    </div>
                    <button
                      type="button"
                      aria-label="Fechar painel"
                      className=" border border-zinc-200 hover:bg-zinc-100 p-2 h-9 w-9 rounded-md text-zinc-950 flex items-center justify-center"
                      onClick={onClose}
                    >
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                  <div className="">
                    <Separator className="mt-4" />
                  </div>
                  <div className="flex h-[calc(100vh-14rem)] flex-col overflow-y-scroll  w-full shadow-xl">
                    {isLoadingAmenities ? (
                      <div className="flex-1 flex items-center justify-center">
                        <Loader2 className="h-6 w-6 animate-spin" />
                      </div>
                    ) : (
                      <div className="flex-1 px-9 py-9 gap-4">
                        {ACTIVE_SKIP_RECENT_UPDATES_FILTER && (
                          <div className="flex flex-col gap-2 p-4">
                            <div className="border border-zinc-200 rounded-md p-4">
                              <div className="flex items-center gap-2 text-xs text-zinc-500 mb-3">
                                <span>
                                  Atualizações recentes consideram mudança no
                                  status do item nos últimos 30 dias,
                                  independente de para qual valor foi alterado.
                                  Por padrão, não são exibidos itens que não
                                  foram alterados nos últimos 30 dias.
                                </span>
                              </div>
                              <div className="flex items-center space-x-2">
                                <Checkbox
                                  id="skip-recent-updates"
                                  checked={filters.skipRecentUpdatesFilter}
                                  onCheckedChange={(checked) =>
                                    updateFilter(
                                      "skipRecentUpdatesFilter",
                                      checked === true,
                                    )
                                  }
                                />
                                <Label
                                  htmlFor="skip-recent-updates"
                                  className="text-sm font-medium text-gray-700 cursor-pointer"
                                >
                                  Incluir ativos sem atualizações recentes
                                </Label>
                              </div>
                            </div>
                          </div>
                        )}

                        <div className="flex flex-col gap-2 p-4">
                          <Label htmlFor="unit-credit-price">
                            Preço do crédito
                          </Label>
                          <Input
                            id="unit-credit-price"
                            type="number"
                            placeholder="0"
                            step="1"
                            value={filters.unitCreditPrice ?? ""}
                            onChange={(e) =>
                              updateFilter(
                                "unitCreditPrice",
                                e.target.value
                                  ? parseInt(e.target.value)
                                  : undefined,
                              )
                            }
                          />
                        </div>

                        <div className="flex flex-col gap-2 p-4">
                          <Label>Capacidade</Label>
                          <div className="flex gap-2">
                            <Input
                              type="number"
                              value={filters.minCapacity ?? ""}
                              placeholder="Mínimo"
                              onChange={(e) =>
                                updateFilter(
                                  "minCapacity",
                                  parseInt(e.target.value),
                                )
                              }
                            />
                            <Input
                              type="number"
                              value={filters.maxCapacity ?? ""}
                              placeholder="Máximo"
                              onChange={(e) =>
                                updateFilter(
                                  "maxCapacity",
                                  parseInt(e.target.value),
                                )
                              }
                            />
                          </div>
                        </div>

                        <div className="flex flex-col gap-2 p-4">
                          <Label>Preço mensal</Label>
                          <div className="flex gap-2">
                            <Input
                              type="number"
                              value={filters.minPrice ?? ""}
                              placeholder="Mínimo"
                              onChange={(e) =>
                                updateFilter(
                                  "minPrice",
                                  parseInt(e.target.value),
                                )
                              }
                            />
                            <Input
                              type="number"
                              value={filters.maxPrice ?? ""}
                              placeholder="Máximo"
                              onChange={(e) =>
                                updateFilter(
                                  "maxPrice",
                                  parseInt(e.target.value),
                                )
                              }
                            />
                          </div>
                        </div>

                        <div className="flex flex-col gap-2 p-4">
                          <Label htmlFor="distance">Distância</Label>
                          <Input
                            id="distance"
                            type="number"
                            value={filters.distance ?? ""}
                            placeholder="30"
                            onChange={(e) =>
                              updateFilter("distance", parseInt(e.target.value))
                            }
                            style={{ width: "50%" }}
                          />
                        </div>

                        <div className="flex flex-col gap-2 p-4">
                          <CheckboxGroup
                            sections={statusOptions}
                            onChange={handleCheckboxGroupChange(
                              "privateOfficeStatus",
                            )}
                            showClearButton={false}
                            className="border border-zinc-200 rounded-md p-4"
                          />
                        </div>

                        {showLifecycleFilter && (
                          <div className="flex flex-col gap-2 p-4">
                            <CheckboxGroup
                              sections={lifecycleOptions}
                              onChange={handleCheckboxGroupChange(
                                "lifecycleStates",
                              )}
                              showClearButton={false}
                              className="border border-zinc-200 rounded-md p-4"
                            />
                          </div>
                        )}

                        <Label className="ml-4">Amenidades</Label>
                        <div className="flex flex-col gap-2 p-4 mt-4">
                          <CheckboxGroup
                            sections={parseToCheckboxSections(
                              amenities ?? [],
                              filters.amenitiesSlugs,
                            )}
                            onChange={handleCheckboxGroupChange(
                              "amenitiesSlugs",
                            )}
                            disabledOptionIds={[]}
                            className="border border-zinc-200 rounded-md p-4"
                          />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-3 p-4 border-zinc-200 justify-end  flex-col bg-white">
                    <Separator className="mt-4" />
                    <div className="flex gap-3 p-4 border-zinc-200 justify-end bg-white">
                      <Button onClick={handleResetFilters} variant="outline">
                        Limpar filtros
                      </Button>
                      <Button
                        onClick={handleApplyFilters}
                        className="text-black"
                      >
                        Aplicar filtros
                      </Button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
